import { GameObject, Rectangle, Vector2 } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { TempestShape } from '../systems/TunnelSystem';

export class Player implements GameObject {
  public id: string;
  public active: boolean = true;
  public position: Vector2;
  public velocity: Vector2 = { x: 0, y: 0 };
  public size: Vector2;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private movement: Vector2 = { x: 0, y: 0 };
  
  // Player properties
  private readonly PLAYER_SIZE = 16;
  private readonly MOVE_SPEED = 4; // radians per second for rim movement
  private readonly FIRE_COOLDOWN = 0.15; // seconds between shots
  private fireTimer: number = 0;
  
  // Tempest-style rim positioning
  private rimAngle: number = 0; // Current position on rim (0-2π)
  private readonly RIM_RADIUS = 100; // Distance from center (matches tunnel base radius)
  private currentShape: TempestShape = 'circle';
  
  // Visual properties
  private animationTimer: number = 0;
  private thrustAnimation: number = 0;

  constructor(
    id: string,
    x: number,
    y: number,
    renderManager: RenderManager,
    audioManager: AudioManager,
    shape: TempestShape = 'circle'
  ) {
    this.id = id;
    this.currentShape = shape;
    // Initialize at bottom of screen (angle 0)
    this.rimAngle = Math.PI / 2; // Start at bottom
    this.position = this.calculateRimPosition(x, y);
    this.size = { x: this.PLAYER_SIZE, y: this.PLAYER_SIZE };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
  }

  public update(deltaTime: number): void {
    // Update fire timer
    if (this.fireTimer > 0) {
      this.fireTimer -= deltaTime;
    }
    
    // Update animation timers
    this.animationTimer += deltaTime;
    
    // Apply rim movement (left/right only)
    const rotationInput = this.movement.x;
    if (rotationInput !== 0) {
      this.rimAngle += rotationInput * this.MOVE_SPEED * deltaTime;
      
      // Keep angle in 0-2π range
      if (this.rimAngle < 0) this.rimAngle += Math.PI * 2;
      if (this.rimAngle >= Math.PI * 2) this.rimAngle -= Math.PI * 2;
    }
    
    // Update position based on rim angle (assuming screen center)
    this.position = this.calculateRimPosition(400, 300);
    
    // Update thrust animation based on movement
    const isMoving = Math.abs(rotationInput) > 0;
    if (isMoving) {
      this.thrustAnimation += deltaTime * 10;
    } else {
      this.thrustAnimation = 0;
    }
  }

  public setMovement(x: number, y: number): void {
    // Only horizontal movement affects rim rotation in Tempest
    this.movement.x = x;
    this.movement.y = 0; // Ignore vertical movement
  }

  public canFire(): boolean {
    return this.fireTimer <= 0;
  }

  public resetFireTimer(): void {
    this.fireTimer = this.FIRE_COOLDOWN;
  }

  public getPosition(): Vector2 {
    return { ...this.position };
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.PLAYER_SIZE,
      height: this.PLAYER_SIZE
    };
  }

  public getSize(): number {
    return this.PLAYER_SIZE;
  }
  
  private calculateRimPosition(centerX: number, centerY: number): Vector2 {
    return this.getPositionOnShape(centerX, centerY, this.rimAngle, this.currentShape);
  }
  
  private getPositionOnShape(centerX: number, centerY: number, angle: number, shape: TempestShape): Vector2 {
    const radius = this.RIM_RADIUS;
    
    switch (shape) {
      case 'circle':
        return {
          x: centerX + Math.cos(angle) * radius,
          y: centerY + Math.sin(angle) * radius
        };
        
      case 'square': {
        // Map angle to square perimeter
        const normalizedAngle = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        const sideLength = radius * 1.4; // Make square fit roughly same area
        const halfSide = sideLength / 2;
        
        if (normalizedAngle < Math.PI / 4 || normalizedAngle >= 7 * Math.PI / 4) {
          // Right side
          const t = (normalizedAngle < Math.PI / 4) ? 
            normalizedAngle / (Math.PI / 4) : 
            (normalizedAngle - 7 * Math.PI / 4) / (Math.PI / 4);
          return {
            x: centerX + halfSide,
            y: centerY + (t - 0.5) * sideLength
          };
        } else if (normalizedAngle < 3 * Math.PI / 4) {
          // Top side
          const t = (normalizedAngle - Math.PI / 4) / (Math.PI / 2);
          return {
            x: centerX + (0.5 - t) * sideLength,
            y: centerY - halfSide
          };
        } else if (normalizedAngle < 5 * Math.PI / 4) {
          // Left side
          const t = (normalizedAngle - 3 * Math.PI / 4) / (Math.PI / 2);
          return {
            x: centerX - halfSide,
            y: centerY + (t - 0.5) * sideLength
          };
        } else {
          // Bottom side
          const t = (normalizedAngle - 5 * Math.PI / 4) / (Math.PI / 2);
          return {
            x: centerX + (t - 0.5) * sideLength,
            y: centerY + halfSide
          };
        }
      }
      
      case 'triangle': {
        const sides = 3;
        const sideAngle = (Math.PI * 2) / sides;
        const sideIndex = Math.floor(angle / sideAngle);
        const t = (angle % sideAngle) / sideAngle;
        
        const p1Angle = sideIndex * sideAngle - Math.PI / 2;
        const p2Angle = (sideIndex + 1) * sideAngle - Math.PI / 2;
        
        const p1 = {
          x: centerX + Math.cos(p1Angle) * radius,
          y: centerY + Math.sin(p1Angle) * radius
        };
        const p2 = {
          x: centerX + Math.cos(p2Angle) * radius,
          y: centerY + Math.sin(p2Angle) * radius
        };
        
        return {
          x: p1.x + (p2.x - p1.x) * t,
          y: p1.y + (p2.y - p1.y) * t
        };
      }
      
      case 'diamond': {
        const normalizedAngle = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        const size = radius * 0.8;
        
        if (normalizedAngle < Math.PI / 2) {
          // Top-right edge
          const t = normalizedAngle / (Math.PI / 2);
          return {
            x: centerX + t * size,
            y: centerY - (1 - t) * size
          };
        } else if (normalizedAngle < Math.PI) {
          // Bottom-right edge
          const t = (normalizedAngle - Math.PI / 2) / (Math.PI / 2);
          return {
            x: centerX + (1 - t) * size,
            y: centerY + t * size
          };
        } else if (normalizedAngle < 3 * Math.PI / 2) {
          // Bottom-left edge
          const t = (normalizedAngle - Math.PI) / (Math.PI / 2);
          return {
            x: centerX - t * size,
            y: centerY + (1 - t) * size
          };
        } else {
          // Top-left edge
          const t = (normalizedAngle - 3 * Math.PI / 2) / (Math.PI / 2);
          return {
            x: centerX - (1 - t) * size,
            y: centerY - t * size
          };
        }
      }
      
      case 'hexagon':
      case 'octagon': {
        const sides = shape === 'hexagon' ? 6 : 8;
        const sideAngle = (Math.PI * 2) / sides;
        const sideIndex = Math.floor(angle / sideAngle);
        const t = (angle % sideAngle) / sideAngle;
        
        const p1Angle = sideIndex * sideAngle;
        const p2Angle = (sideIndex + 1) * sideAngle;
        
        const p1 = {
          x: centerX + Math.cos(p1Angle) * radius,
          y: centerY + Math.sin(p1Angle) * radius
        };
        const p2 = {
          x: centerX + Math.cos(p2Angle) * radius,
          y: centerY + Math.sin(p2Angle) * radius
        };
        
        return {
          x: p1.x + (p2.x - p1.x) * t,
          y: p1.y + (p2.y - p1.y) * t
        };
      }
      
      case 'star': {
        const points = 10;
        const sideAngle = (Math.PI * 2) / points;
        const sideIndex = Math.floor(angle / sideAngle);
        const t = (angle % sideAngle) / sideAngle;
        
        const r1 = (sideIndex % 2 === 0) ? radius : radius * 0.5;
        const r2 = ((sideIndex + 1) % 2 === 0) ? radius : radius * 0.5;
        
        const p1Angle = sideIndex * sideAngle;
        const p2Angle = (sideIndex + 1) * sideAngle;
        
        const p1 = {
          x: centerX + Math.cos(p1Angle) * r1,
          y: centerY + Math.sin(p1Angle) * r1
        };
        const p2 = {
          x: centerX + Math.cos(p2Angle) * r2,
          y: centerY + Math.sin(p2Angle) * r2
        };
        
        return {
          x: p1.x + (p2.x - p1.x) * t,
          y: p1.y + (p2.y - p1.y) * t
        };
      }
      
      case 'plus': {
        // For plus shape, we need to trace around the perimeter
        const normalizedAngle = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        const thickness = radius * 0.3;
        const armLength = radius;
        
        // Divide into 12 segments (3 per arm)
        const segmentAngle = (Math.PI * 2) / 12;
        const segment = Math.floor(normalizedAngle / segmentAngle);
        const t = (normalizedAngle % segmentAngle) / segmentAngle;
        
        // Define the plus shape vertices in order
        const vertices = [
          { x: thickness, y: -armLength },    // 0: top-right of vertical arm
          { x: thickness, y: -thickness },    // 1: inner corner
          { x: armLength, y: -thickness },    // 2: top-right of horizontal arm
          { x: armLength, y: thickness },     // 3: bottom-right of horizontal arm
          { x: thickness, y: thickness },     // 4: inner corner
          { x: thickness, y: armLength },     // 5: bottom-right of vertical arm
          { x: -thickness, y: armLength },    // 6: bottom-left of vertical arm
          { x: -thickness, y: thickness },    // 7: inner corner
          { x: -armLength, y: thickness },    // 8: bottom-left of horizontal arm
          { x: -armLength, y: -thickness },   // 9: top-left of horizontal arm
          { x: -thickness, y: -thickness },   // 10: inner corner
          { x: -thickness, y: -armLength }    // 11: top-left of vertical arm
        ];
        
        const v1 = vertices[segment];
        const v2 = vertices[(segment + 1) % vertices.length];
        
        return {
          x: centerX + v1.x + (v2.x - v1.x) * t,
          y: centerY + v1.y + (v2.y - v1.y) * t
        };
      }
      
      default:
        return {
          x: centerX + Math.cos(angle) * radius,
          y: centerY + Math.sin(angle) * radius
        };
    }
  }
  
  public setShape(shape: TempestShape): void {
    this.currentShape = shape;
  }

  public getRimAngle(): number {
    return this.rimAngle;
  }
  
  public getShape(): TempestShape {
    return this.currentShape;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    const centerX = this.position.x + this.PLAYER_SIZE / 2;
    const centerY = this.position.y + this.PLAYER_SIZE / 2;
    
    // Draw Tempest-style player shooter (triangle pointing toward center)
    const shipColor = '#FFFF00'; // Yellow like original Tempest
    const shipSize = this.PLAYER_SIZE / 2;
    
    // Calculate angle pointing toward center
    const centerAngle = this.rimAngle + Math.PI;
    
    // Ship body (triangle pointing inward)
    const noseX = centerX + Math.cos(centerAngle) * shipSize;
    const noseY = centerY + Math.sin(centerAngle) * shipSize;
    
    const leftWingX = centerX + Math.cos(centerAngle + 2.5) * shipSize * 0.6;
    const leftWingY = centerY + Math.sin(centerAngle + 2.5) * shipSize * 0.6;
    
    const rightWingX = centerX + Math.cos(centerAngle - 2.5) * shipSize * 0.6;
    const rightWingY = centerY + Math.sin(centerAngle - 2.5) * shipSize * 0.6;
    
    this.renderManager.drawPolygon([
      { x: noseX, y: noseY },     // Nose pointing toward center
      { x: leftWingX, y: leftWingY },   // Left wing
      { x: rightWingX, y: rightWingY }  // Right wing
    ], shipColor, true);
    
    // Ship outline
    this.renderManager.drawPolygon([
      { x: noseX, y: noseY },
      { x: leftWingX, y: leftWingY },
      { x: rightWingX, y: rightWingY }
    ], '#FFFFFF', false, 1);
    
    // Cockpit
    this.renderManager.drawEllipse(
      centerX - 3,
      centerY - 3,
      3,
      3,
      '#62D0FE',
      true
    );
    
    // Movement trail effects (when rotating around rim)
    if (this.thrustAnimation > 0) {
      const trailLength = 8 + Math.sin(this.thrustAnimation) * 4;
      const trailColor = Math.sin(this.thrustAnimation * 2) > 0 ? '#FF4500' : '#FFD700';
      
      // Trail behind the ship when moving
      const trailAngle = this.rimAngle + (this.movement.x > 0 ? -0.3 : 0.3);
      const trailX = centerX + Math.cos(trailAngle) * (this.RIM_RADIUS * 0.9);
      const trailY = centerY + Math.sin(trailAngle) * (this.RIM_RADIUS * 0.9);
      
      this.renderManager.drawLine(centerX, centerY, trailX, trailY, trailColor, 2);
    }
    
    // Subtle glow effect like original Tempest
    const glowAlpha = 0.1 + Math.sin(this.animationTimer * 4) * 0.05;
    this.renderManager.drawEllipse(
      centerX - shipSize,
      centerY - shipSize,
      shipSize,
      shipSize,
      `rgba(255, 255, 0, ${glowAlpha})`,
      false,
      1
    );
  }
}